import { Injectable, OnDestroy , inject} from '@angular/core';
import { AppConfigService } from 'app/core/config/app-config.service';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, interval, Subscription, firstValueFrom } from 'rxjs';
import {
  Room,
  LocalTrackPublication,
  RemoteParticipant,
  ParticipantEvent,
  RoomEvent,
  DataPacket_Kind,
} from 'livekit-client';
import { environment } from '@env/environment';
import { FeatureSettingsService } from './feature-settings.service';
import { MediaDeviceCoordinatorService } from './media-device-coordinator.service';

const VC_MEDIA_CONSUMER = 'vclassroom';

// ─── Types ──────────────────────────────────────────────

export interface JoinResponse {
  success: boolean;
  wsUrl: string;
  token: string;
  roomName: string;
}

export interface ParticipantMetadata {
  role: 'student' | 'supervisor' | 'courseadmin' | 'superuser';
  isVisible: boolean;
  name: string;
}

export interface ParticipantTile {
  identity: string;
  name: string;
  metadata: ParticipantMetadata;
  videoTrack?: any;
  audioTrack?: any;
  isMuted?: boolean;
  isCameraOff?: boolean;
}

export interface RoomMode {
  supervisorVisible: boolean;
  showPeers: boolean;
}

export interface TwinInvite {
  invite_id: number;
  twin_name: string;
}

// ─── VC data message types ───────────────────────────────
export type VcMessageType =
  | 'VC_SHOW_PEERS'
  | 'VC_SHOW_SUPERVISOR'
  | 'VC_RETURN_TO_MAIN'
  | 'VC_INVITE'
  | 'VC_TWIN_INVITE'
  | 'VC_TWIN_JOIN'
  | 'VC_TWIN_DECLINED'
  | 'VC_TWIN_EXPIRED';

export interface VcDataMessage {
  type: VcMessageType;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class VirtualClassroomService implements OnDestroy {
  readonly appConfig = inject(AppConfigService);
  // ─── Core room state ──────────────────────────────────
  private room: Room | null = null;
  private currentRoomName: string | null = null;
  private identity: string = '';
  private userRole: string = 'student';

  /** User payload from initializeSession — needed to join later (auth uses autoJoin:false). */
  private sessionUser: any = null;

  /** Prevents overlapping join attempts from selfie retries / course entry. */
  private joinInFlight: Promise<void> | null = null;

  /** The first room joined — never overwritten by secondary (twin/consulting) rooms */
  primaryRoomName: string | null = null;

  private initialized = false;

  // ─── Observables ──────────────────────────────────────

  public roomChanged$ = new BehaviorSubject<string | null>(null);
  public participantsOnline$ = new BehaviorSubject<string[]>([]);

  /** Emits all visible participant tiles (ghosts filtered out for students) */
  public participantTiles$ = new BehaviorSubject<ParticipantTile[]>([]);

  /** Supervisor visibility + showPeers mode — updated by VC_SHOW_SUPERVISOR/VC_SHOW_PEERS */
  public roomMode$ = new BehaviorSubject<RoomMode>({ supervisorVisible: false, showPeers: false });

  /** Pending consulting session invite */
  public consultingInvite$ = new BehaviorSubject<VcDataMessage | null>(null);

  /** Pending twin invite */
  public twinInvite$ = new BehaviorSubject<TwinInvite | null>(null);

  /** Active twin room name (non-null when both twins accepted) */
  public activeTwinRoom$ = new BehaviorSubject<string | null>(null);

  /** True when student is in an active consulting session — blocks inactivity logout */
  public isInProtectedSession$ = new BehaviorSubject<boolean>(false);

  /** Tracks are paused (Tier 1 inactivity) */
  public tracksPaused$ = new BehaviorSubject<boolean>(false);

  constructor(
    private http: HttpClient,
    private featureSettings: FeatureSettingsService,
    private mediaHub: MediaDeviceCoordinatorService,
  ) {}

  // ─── Initialization ───────────────────────────────────

  async initializeSession(user: any, autoJoin: boolean = true): Promise<void> {
    if (!this.featureSettings.isFeatureEnabled('feature_classroom')) {
      console.log('[VClassroom] feature_classroom disabled — skipping init');
      return;
    }

    if (this.initialized) {
      // Keep latest user context so a later ensureConnected() can still join.
      if (user) {
        this.sessionUser = { ...this.sessionUser, ...user, role: user.role ?? this.userRole ?? 'student' };
      }
      console.log('[VClassroom] Already initialized — skipping');
      return;
    }
    this.initialized = true;
    this.userRole = user.role ?? 'student';
    this.identity = this.buildIdentity(user);
    this.sessionUser = { ...user, role: this.userRole };

    console.log('[VClassroom] Initializing session for role:', this.userRole, {
      autoJoin,
      hasSupervisorRoom: !!user?.supervisor_room_key,
    });

    if (autoJoin) {
      await this.attemptAutoJoin(this.sessionUser);
    }
  }

  async joinRoomWhenReady(user?: any): Promise<void> {
    if (!this.featureSettings.isFeatureEnabled('feature_classroom')) return;

    if (!this.initialized) {
      console.warn('[VClassroom] Session not initialized');
      return;
    }
    if (this.room && this.currentRoomName && this.room.state === 'connected') return;

    const joinUser = user ?? this.sessionUser;
    if (!joinUser) {
      console.warn('[VClassroom] joinRoomWhenReady: no session user stored');
      return;
    }
    await this.attemptAutoJoin(joinUser);
  }

  /**
   * Ensure a LiveKit room is connected (auth initializes with autoJoin:false).
   * Safe to call from selfie / course entry — coalesces concurrent callers.
   */
  async ensureConnected(): Promise<boolean> {
    if (!this.featureSettings.isFeatureEnabled('feature_classroom')) {
      return false;
    }
    if (!this.initialized) {
      console.warn('[VClassroom] ensureConnected: not initialized yet');
      return false;
    }
    if (this.room && this.room.state === 'connected') {
      return true;
    }
    // Stale disconnected Room object — clear before re-join
    if (this.room && this.room.state !== 'connected') {
      console.warn('[VClassroom] ensureConnected: room not connected — reconnecting', {
        state: this.room.state,
      });
      await this.disconnectRoom();
    }
    if (this.joinInFlight) {
      await this.joinInFlight;
      return !!(this.room && this.room.state === 'connected');
    }

    console.log('[VClassroom] ensureConnected: joining room…', {
      hasSessionUser: !!this.sessionUser,
      primaryRoomName: this.primaryRoomName,
      identity: this.identity,
    });

    this.joinInFlight = (async () => {
      if (this.primaryRoomName) {
        await this.joinRoomByName(this.primaryRoomName, this.userRole, true);
      } else {
        await this.joinRoomWhenReady(this.sessionUser);
      }
    })().finally(() => {
      this.joinInFlight = null;
    });

    await this.joinInFlight;
    const ok = !!(this.room && this.room.state === 'connected');
    console.log(ok ? '[VClassroom] ensureConnected: OK' : '[VClassroom] ensureConnected: FAILED', {
      roomName: this.currentRoomName,
      state: this.room?.state,
    });
    return ok;
  }

  // ─── Identity ─────────────────────────────────────────

  private buildIdentity(user: any): string {
    const sanitizedName = (user.name ?? 'user').replace(/[^a-z0-9]/gi, '').toLowerCase();
    const role = user.role ?? 'student';
    const randomHex = Math.random().toString(16).substring(2, 8);
    return `${sanitizedName}-${role}-${user.company_id ?? 0}.${user.fid ?? 0}-${randomHex}`;
  }

  // ─── Room resolution ──────────────────────────────────

  private async attemptAutoJoin(user: any): Promise<void> {
    const targetRoom = await this.resolveTargetRoom(user);
    if (targetRoom) {
      await this.joinRoomByName(targetRoom, user.role ?? 'student');
    } else {
      console.warn('[VClassroom] No target room found');
    }
  }

  private async resolveTargetRoom(user: any): Promise<string | null> {
    const supervisorRoomKey = user.supervisor_room_key ?? null;
    if (supervisorRoomKey) {
      const roomName = supervisorRoomKey.startsWith('supervisor-')
        ? supervisorRoomKey
        : `supervisor-${supervisorRoomKey}`;
      console.log('[VClassroom] Joining supervisor room:', roomName);
      return roomName;
    }

    const defaultRoom = `user-${user.fid}-${user.company_id}`;
    console.log('[VClassroom] Using default room:', defaultRoom);
    return defaultRoom;
  }

  // ─── checkTwinOnline ──────────────────────────────────

  async checkTwinOnline(twinId: string | number): Promise<boolean> {
    if (!this.featureSettings.isFeatureEnabled('feature_twin')) return false;
    try {
      const res = await firstValueFrom(
        this.http.get<{ success: boolean; online: boolean }>(
          `${this.appConfig.get('serverUrl')}/v5/twin-status/${twinId}`
        )
      );
      return res?.online === true;
    } catch {
      return false;
    }
  }

  // ─── Join / disconnect ────────────────────────────────

  async joinRoomByName(roomName: string, role?: string, isVisible?: boolean): Promise<void> {
    if (this.currentRoomName === roomName && this.room) {
      const hasVideo = this.room.localParticipant.videoTrackPublications.size > 0;
      if (!hasVideo && role === 'student') await this.publishCameraAndMicrophone();
      return;
    }

    console.log('[VClassroom] Joining room:', roomName);
    await this.disconnectRoom();

    const resolvedRole  = role ?? this.userRole ?? 'student';
    const resolvedVisible = isVisible !== undefined ? isVisible : (resolvedRole === 'student');

    try {
      const response = await firstValueFrom(
        this.http.post<JoinResponse>(`${this.appConfig.get('serverUrl')}/v5/vcl`, {
          room_name:  roomName,
          identity:   this.identity,
          name:       this.identity,
          role:       resolvedRole,
          is_visible: resolvedVisible,
        })
      );

      if (!response?.success) {
        console.error('[VClassroom] Join failed:', response);
        return;
      }

      this.room = new Room();
      this._attachRoomEvents(this.room);

      await this.room.connect(response.wsUrl, response.token, { autoSubscribe: true });
      this.currentRoomName = roomName;
      this.roomChanged$.next(roomName);

      // Record primary room only on first join
      if (!this.primaryRoomName) {
        this.primaryRoomName = roomName;
      }

      if (resolvedRole === 'student' && resolvedVisible) {
        await this.publishCameraAndMicrophone();
      }

      this._refreshParticipantTiles();
      console.log('[VClassroom] ✅ Joined:', roomName);
    } catch (err) {
      console.error('[VClassroom] Error joining room:', err);
    }
  }

  async returnToPrimaryRoom(): Promise<void> {
    if (!this.primaryRoomName) {
      console.warn('[VClassroom] No primary room recorded');
      return;
    }
    this.isInProtectedSession$.next(false);
    this.activeTwinRoom$.next(null);
    await this.joinRoomByName(this.primaryRoomName, this.userRole, true);
  }

  async disconnectRoom(): Promise<void> {
    if (this.room) {
      for (const pub of this.room.localParticipant.videoTrackPublications.values()) {
        this._stopAndUnpublish(pub);
      }
      for (const pub of this.room.localParticipant.audioTrackPublications.values()) {
        this._stopAndUnpublish(pub);
      }
      this.room.disconnect();
      this.room = null;
      this.currentRoomName = null;
      this.roomChanged$.next(null);
      this.participantTiles$.next([]);
    }
    // Release classroom clones; AI may still hold the shared source.
    this.mediaHub.release(VC_MEDIA_CONSUMER);
  }

  // ─── Track publish / unpublish ────────────────────────

  private async publishCameraAndMicrophone(): Promise<void> {
    if (!this.room || this.room.state !== 'connected') return;

    try {
      if (location.protocol !== 'https:' && location.hostname !== 'localhost') {
        throw new Error('Camera requires HTTPS');
      }

      // Shared hub: one GUM for classroom + AI; mic clones keep mute independent.
      await this.mediaHub.ensureCapture({ video: true, audio: true });
      const videoTrack = await this.mediaHub.checkoutVideoTrack(VC_MEDIA_CONSUMER);
      const micStream = await this.mediaHub.checkoutMicStream(VC_MEDIA_CONSUMER);
      const audioTrack = micStream.getAudioTracks()[0] ?? null;

      if (videoTrack) await this.room.localParticipant.publishTrack(videoTrack);
      if (audioTrack) {
        audioTrack.enabled = false; // mic off by default (clone only — AI unaffected)
        await this.room.localParticipant.publishTrack(audioTrack);
      }
      this.tracksPaused$.next(false);
    } catch (err: any) {
      console.error('[VClassroom] Camera access failed:', err.name, err.message);
    }
  }

  async unpublishTracks(): Promise<void> {
    if (!this.room) return;
    for (const pub of this.room.localParticipant.videoTrackPublications.values()) {
      if (pub.track) {
        // stopOnUnpublish=false: hub owns clone lifecycle via release()
        await this.room.localParticipant.unpublishTrack(pub.track, false);
      }
    }
    for (const pub of this.room.localParticipant.audioTrackPublications.values()) {
      if (pub.track) {
        await this.room.localParticipant.unpublishTrack(pub.track, false);
      }
    }
    this.mediaHub.release(VC_MEDIA_CONSUMER);
    this.tracksPaused$.next(true);
  }

  async republishTracks(): Promise<void> {
    if (!this.room || this.room.state !== 'connected') return;
    await this.publishCameraAndMicrophone();
  }

  private _stopAndUnpublish(pub: LocalTrackPublication): void {
    const track = pub.track;
    if (track) {
      // Do not stop here — mediaHub.release() stops the classroom clones.
      this.room?.localParticipant.unpublishTrack(track, false);
      track.detach();
    }
  }

  // ─── Room event listeners ─────────────────────────────

  private _attachRoomEvents(room: Room): void {
    room.on(RoomEvent.ParticipantConnected, () => this._refreshParticipantTiles());
    room.on(RoomEvent.ParticipantDisconnected, () => this._refreshParticipantTiles());
    room.on(RoomEvent.TrackSubscribed, () => this._refreshParticipantTiles());
    room.on(RoomEvent.TrackUnsubscribed, () => this._refreshParticipantTiles());

    room.on(RoomEvent.DataReceived, (payload: Uint8Array, participant?: RemoteParticipant) => {
      try {
        const msg: VcDataMessage = JSON.parse(new TextDecoder().decode(payload));
        this._handleDataMessage(msg);
      } catch (e) {
        console.warn('[VClassroom] Could not parse data message', e);
      }
    });
  }

  private _handleDataMessage(msg: VcDataMessage): void {
    console.log('[VClassroom] DataReceived:', msg.type, msg);
    switch (msg.type) {
      case 'VC_SHOW_SUPERVISOR':
        this.roomMode$.next({ ...this.roomMode$.value, supervisorVisible: true });
        break;

      case 'VC_SHOW_PEERS':
        this.roomMode$.next({ ...this.roomMode$.value, showPeers: !!msg.show });
        break;

      case 'VC_RETURN_TO_MAIN':
        this.returnToPrimaryRoom();
        break;

      case 'VC_INVITE':
        this.consultingInvite$.next(msg);
        this.isInProtectedSession$.next(true);
        break;

      case 'VC_TWIN_INVITE':
        this.twinInvite$.next({ invite_id: msg.invite_id, twin_name: msg.twin_name });
        break;

      case 'VC_TWIN_JOIN':
        this.activeTwinRoom$.next(msg.room_name);
        this.twinInvite$.next(null);
        break;

      case 'VC_TWIN_DECLINED':
      case 'VC_TWIN_EXPIRED':
        this.twinInvite$.next(null);
        break;
    }
  }

  // ─── Participant tiles ────────────────────────────────

  private _refreshParticipantTiles(): void {
    if (!this.room) {
      this.participantTiles$.next([]);
      return;
    }

    const tiles: ParticipantTile[] = [];
    this.room.remoteParticipants.forEach((participant: RemoteParticipant) => {
      let meta: ParticipantMetadata = { role: 'student', isVisible: true, name: participant.name ?? participant.identity };
      try {
        if (participant.metadata) {
          meta = JSON.parse(participant.metadata);
        }
      } catch {}

      // Students only see participants who are visible (ghost supervisors filtered)
      if (this.userRole === 'student' && !meta.isVisible) return;

      const videoTrack = Array.from(participant.videoTrackPublications.values())
        .find(p => p.isSubscribed && p.track)?.track;
      const audioTrack = Array.from(participant.audioTrackPublications.values())
        .find(p => p.isSubscribed && p.track)?.track;

      tiles.push({
        identity:    participant.identity,
        name:        meta.name || participant.identity,
        metadata:    meta,
        videoTrack,
        audioTrack,
        isMuted:     !audioTrack || audioTrack.isMuted,
        isCameraOff: !videoTrack || (videoTrack as any).isMuted,
      });
    });

    this.participantTiles$.next(tiles);
  }

  // ─── Invite actions ───────────────────────────────────

  async acceptConsultingInvite(roomName: string): Promise<void> {
    this.consultingInvite$.next(null);
    this.isInProtectedSession$.next(true);
    await this.joinRoomByName(roomName, 'student', true);
  }

  declineConsultingInvite(): void {
    this.consultingInvite$.next(null);
  }

  async acceptTwinInvite(inviteId: number): Promise<void> {
    try {
      await firstValueFrom(
        this.http.post(`${this.appConfig.get('serverUrl')}/v5/vc/twin-invite/${inviteId}/accept`, {})
      );
    } catch (e) {
      console.error('[VClassroom] Accept twin invite failed', e);
    }
  }

  async declineTwinInvite(inviteId: number): Promise<void> {
    this.twinInvite$.next(null);
    try {
      await firstValueFrom(
        this.http.post(`${this.appConfig.get('serverUrl')}/v5/vc/twin-invite/${inviteId}/decline`, {})
      );
    } catch (e) {
      console.error('[VClassroom] Decline twin invite failed', e);
    }
  }

  async leaveTwinRoom(): Promise<void> {
    const invite = this.twinInvite$.value;
    if (invite) {
      await this.declineTwinInvite(invite.invite_id);
    }
    this.activeTwinRoom$.next(null);
    await this.returnToPrimaryRoom();
  }

  async leaveConsultingRoom(): Promise<void> {
    this.isInProtectedSession$.next(false);
    this.consultingInvite$.next(null);
    await this.returnToPrimaryRoom();
  }

  // ─── Accessors ────────────────────────────────────────

  getRoom(): Room | null { return this.room; }
  getCurrentRoomName(): string | null { return this.currentRoomName; }
  isInitialized(): boolean { return this.initialized; }
  getIdentity(): string { return this.identity; }
  getUserRole(): string { return this.userRole; }

  ngOnDestroy(): void {
    this.disconnectRoom();
  }
}
